import { useState, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from './DashboardLayout';
import {
  OverviewSection,
  UpcomingBookingsSection,
  BookingHistorySection,
  NotificationsSection,
  ProfileSection,
  PaymentMethodsSection
} from './sections';
import type { DashboardSection } from '../../types/dashboard.types';

const VALID_SECTIONS: DashboardSection[] = ['overview', 'bookings', 'history', 'profile', 'notifications', 'payment-methods', 'support'];

const isValidSection = (s: string): s is DashboardSection => VALID_SECTIONS.includes(s as DashboardSection);

// Support section placeholder (minimal feature)
const SupportSection = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
    <h2 className="text-xl font-semibold text-gray-900 mb-2">Help & Support</h2>
    <p className="text-gray-600 mb-6">Need help? We're here for you.</p>
    <div className="space-y-4">
      <a
        href="mailto:support@massatherapy.com"
        className="block px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
      >
        Email: support@massatherapy.com
      </a>
      <a
        href="tel:+18001234567"
        className="block px-4 py-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
      >
        Phone: 1-800-123-4567
      </a>
    </div>
  </div>
);

function renderSection(section: DashboardSection) {
  switch (section) {
    case 'overview': return <OverviewSection />;
    case 'bookings': return <UpcomingBookingsSection />;
    case 'history': return <BookingHistorySection />;
    case 'profile': return <ProfileSection />;
    case 'notifications': return <NotificationsSection />;
    case 'payment-methods': return <PaymentMethodsSection />;
    case 'support': return <SupportSection />;
    default: return <OverviewSection />;
  }
}

export function Dashboard() {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const sectionParam = searchParams.get('section');
  const [activeSection, setActiveSection] = useState<DashboardSection>(
    sectionParam && isValidSection(sectionParam) ? sectionParam : 'overview'
  );

  // Sync URL → state: When URL changes (e.g., from Quick Action link), update active section
  useEffect(() => {
    if (sectionParam && isValidSection(sectionParam) && sectionParam !== activeSection) {
      setActiveSection(sectionParam);
    }
  }, [sectionParam, activeSection]);

  const handleSectionChange = (section: DashboardSection) => {
    setActiveSection(section);
    setSearchParams({ section }, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <DashboardLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
      {renderSection(activeSection)}
    </DashboardLayout>
  );
}

export default Dashboard;
